import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
} from '@mui/icons-material';
import { appAuthAPI } from '../services/api';

function EmailConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const [config, setConfig] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_use_tls: true,
    smtp_use_ssl: false,
    smtp_username: '',
    smtp_password: '',
    from_email: 'noreply@example.com',
    from_name: 'Samba4 WebUI',
    totp_expiry_minutes: 10,
    enabled: false,
    test_mode: false,
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await appAuthAPI.getEmailConfig();
      setConfig(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: value });
    setSuccess('');
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await appAuthAPI.updateEmailConfig(config);
      setConfig(response.data);
      setSuccess('Email configuration saved successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    setTesting(true);
    setError('');
    setSuccess('');
    try {
      await appAuthAPI.testEmailConfig(testEmail);
      setSuccess(`Test email sent to ${testEmail}`);
      setTestDialogOpen(false);
      setTestEmail('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <EmailIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">Email Configuration</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure SMTP settings for password reset and notifications
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card>
        <CardContent>
          {/* Status */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                />
              }
              label={<Typography variant="h6">Enable Email Service</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.test_mode}
                  onChange={(e) => handleChange('test_mode', e.target.checked)}
                />
              }
              label="Test Mode (emails logged to console)"
              sx={{ ml: 2 }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* SMTP Server Settings */}
          <Typography variant="h6" gutterBottom>
            SMTP Server Settings
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="SMTP Host"
                value={config.smtp_host}
                onChange={(e) => handleChange('smtp_host', e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="SMTP Port"
                value={config.smtp_port}
                onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
                placeholder="587"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.smtp_use_tls}
                    onChange={(e) => handleChange('smtp_use_tls', e.target.checked)}
                  />
                }
                label="Use TLS (recommended for port 587)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.smtp_use_ssl}
                    onChange={(e) => handleChange('smtp_use_ssl', e.target.checked)}
                  />
                }
                label="Use SSL (for port 465)"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* SMTP Authentication */}
          <Typography variant="h6" gutterBottom>
            SMTP Authentication
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SMTP Username"
                value={config.smtp_username}
                onChange={(e) => handleChange('smtp_username', e.target.value)}
                placeholder="your-email@gmail.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="SMTP Password"
                value={config.smtp_password}
                onChange={(e) => handleChange('smtp_password', e.target.value)}
                placeholder="App password or SMTP password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* From Email Settings */}
          <Typography variant="h6" gutterBottom>
            Email Sender Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="From Email Address"
                value={config.from_email}
                onChange={(e) => handleChange('from_email', e.target.value)}
                placeholder="noreply@example.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Name"
                value={config.from_name}
                onChange={(e) => handleChange('from_name', e.target.value)}
                placeholder="Samba4 WebUI"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* TOTP Settings */}
          <Typography variant="h6" gutterBottom>
            Password Reset Settings
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="TOTP Code Expiry (minutes)"
                value={config.totp_expiry_minutes}
                onChange={(e) => handleChange('totp_expiry_minutes', parseInt(e.target.value))}
                placeholder="10"
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => setTestDialogOpen(true)}
              disabled={!config.enabled}
            >
              Send Test Email
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onClose={() => !testing && setTestDialogOpen(false)}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Test Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="your-email@example.com"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)} disabled={testing}>
            Cancel
          </Button>
          <Button
            onClick={handleTestEmail}
            variant="contained"
            disabled={testing || !testEmail}
            startIcon={testing ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {testing ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailConfig;
